import React, { useState } from 'react';
import Loading from '../components/Loading';
import { getTarotReading, getExtendedTarotReading, isGeminiReady } from '../services/geminiService';
import { getRandomTarotCards, getTarotCardsForSpread, TAROT_SPREADS } from '../data/astroData';
import { useLanguage } from '../context/LanguageContext';
import ShareReading from '../components/ShareReading';
import ReadingDisclaimer from '../components/ReadingDisclaimer';

const spreadOptions = [
  { id: 'threeCard', name: 'Three Card (Past/Present/Future)', cards: 3 },
  { id: 'celticCross', name: 'Celtic Cross (10 cards)', cards: 10 },
  { id: 'loveSpread', name: 'Love Spread (5 cards)', cards: 5 },
  { id: 'careerSpread', name: 'Career Spread (5 cards)', cards: 5 },
  { id: 'yesNo', name: 'Yes/No (1 card)', cards: 1 },
];

export default function Tarot() {
  const [spreadType, setSpreadType] = useState('threeCard');
  const [question, setQuestion] = useState('');
  const [cards, setCards] = useState([]);
  const [reading, setReading] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revealed, setRevealed] = useState(false);
  const { language, t } = useLanguage();

  const handleDraw = async () => {
    if (!isGeminiReady()) {
      setError(t('apiKeyError'));
      return;
    }

    const drawnCards = getTarotCardsForSpread(spreadType);
    setCards(drawnCards);
    setRevealed(true);
    setLoading(true);
    setError('');
    setReading('');
    try {
      const result = spreadType === 'threeCard'
        ? await getTarotReading(drawnCards, question, language)
        : await getExtendedTarotReading(drawnCards, spreadType, question, language);
      setReading(result);
    } catch (err) {
      setError(err.message || 'Failed to interpret tarot cards.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCards([]);
    setReading('');
    setRevealed(false);
    setError('');
  };

  const currentSpread = TAROT_SPREADS[spreadType];

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h2>{t('tarotTitle')}</h2>
          <p>{t('tarotDesc')}</p>
        </div>

        {!revealed ? (
          <div className="card" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🃏</div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              {t('tarotIntro')}
            </p>

            {/* Spread Selection */}
            <div className="form-group" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
              <label>{t('tarotSpread')}</label>
              <select
                value={spreadType}
                onChange={(e) => setSpreadType(e.target.value)}
                className="spread-select"
              >
                {spreadOptions.map(spread => (
                  <option key={spread.id} value={spread.id}>{spread.name}</option>
                ))}
              </select>
              <p className="spread-description" style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                {currentSpread?.description}
              </p>
            </div>

            <div className="form-group" style={{ textAlign: 'left' }}>
              <label>{t('yourQuestion')}</label>
              <input
                type="text"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder={t('questionPlaceholder')}
              />
            </div>
            <button className="btn btn-gold" onClick={handleDraw} style={{ width: '100%' }}>
              {t('drawCards')}
            </button>
          </div>
        ) : (
          <>
            {/* Spread Name */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <span className="spread-badge">{currentSpread?.name}</span>
            </div>

            {/* Drawn Cards Display */}
            <div className={`tarot-cards-display ${spreadType === 'celticCross' ? 'celtic-layout' : ''}`}>
              {cards.map((card, i) => (
                <div
                  key={i}
                  className={`tarot-single-card tarot-card-with-image ${spreadType === 'celticCross' ? `celtic-pos-${i}` : ''}`}
                  style={{ transform: card.reversed ? 'rotate(180deg)' : 'none' }}
                >
                  <img
                    src={card.image}
                    alt={card.name}
                    className="tarot-card-image"
                  />
                  <span className="card-name">{card.name}</span>
                  <span className="card-position">
                    {card.reversed ? t('reversed') : t('upright')}
                  </span>
                </div>
              ))}
            </div>

            {/* Card Labels Below */}
            <div className="card-labels-grid" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              {cards.map((card, i) => (
                <div key={i} style={{ textAlign: 'center', minWidth: '120px' }}>
                  <div style={{ color: 'var(--accent-gold)', fontFamily: 'var(--font-heading)', fontSize: '0.8rem' }}>
                    {card.position}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    {card.name} {card.reversed ? '↓' : '↑'}
                  </div>
                </div>
              ))}
            </div>

            {loading && <Loading message={t('loadingTarot')} />}
            {error && <div className="error-message">{error}</div>}

            {reading && (
              <div className="result-container">
                <div className="result-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3>{t('tarotResult')}</h3>
                    <ShareReading title={`${currentSpread?.name} Tarot Reading`} content={reading} type="tarot" />
                  </div>
                  {question && (
                    <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: '1rem' }}>
                      Question: "{question}"
                    </p>
                  )}
                  <div className="content">{reading}</div>
                  <ReadingDisclaimer />
                </div>
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button className="btn btn-secondary" onClick={handleReset}>
                {t('drawNewCards')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
